import { ImagePathWithRenditions } from './ImageTypes';
import { ProductTypes } from './enums/ProductEnums';
import { CustomerAddress } from './checkout';

export type OrderedItems = Array<Sale>;

export type OrderBase = {
  currencySignHtml: string;
  deliveryPrice: number;
  shopName: string;
  totalItems: number;
  totalDiscount: number;
  totalStandardPrice: number;
};

export type LineBase = {
  colourId: number;
  orderStatusDescription?: string;
  picture: string;
  productCode: string;
  productType: ProductTypes;
  quantity: number;
  sellPrice: number;
  skuId: number;
  totalStandardPrice: number;
  trackingCode?: string;
  trackingUrl?: string;
};

export type Sale = OrderBase & {
  saleId: string;
  orderId: string;
  webOrder: boolean;
  saleDate: number;
  lastPossibleReturnDate?: number;
  totalSellPrice: number;
  currency: string;
  hasInvoice: boolean;
  returned: boolean;
  saleLines: Array<SaleLine>;
  payments: Array<Payment>;
  originalPaymentMethod?: string;
};

export type SaleLine = LineBase & {
  saleLineId: string;
  productDescription: string;
  productColour: string;
  productSize: string;
  url: string;
  brand: string;
  totalSellPrice: number;
  typeId: string;
  rootUrl: string;
  courierName: string;
  hasInvoice: boolean;
  returned: boolean;
  cantBeReturned: boolean;
  markedAsReturn: boolean;
  returnAnnouncedDate: number;
  originalPaymentMethod?: string;
};

export type Payment = {
  amount: number;
  typeId: number;
  code: string;
};

export type ReturnedItem = {
  saleId: string;
  orderId: string;
};

export type ReturnedItems = Array<ReturnedItem>;

export type ReturnReason = {
  returnReasonId: number;
  returnReasonCode: string;
  description: string;
  freeText?: boolean;
};

export type NewlyAnnouncedReturn = {
  saleLineIds: Array<NewlyAnnouncedReturnLine>;
  address: CustomerAddress | undefined;
  returnCountryId: number | undefined;
  returnOptionId: number;
};

export type NewlyAnnouncedReturnLine = {
  qty: number;
  returnReasonFreeText?: string;
  returnReasonId: ReturnReason['returnReasonId'] | undefined;
  saleId: string;
  saleLineId: string;
  originalPaymentMethod?: string;
};
export enum ReturnLocations {
  STORE = 'STORE',
  WAREHOUSE = 'WAREHOUSE',
}

export enum RefundInformationMessageType {
  KLARNA = 'KLARNA',
  SAFERPAY = 'SAFERPAY',
  OTHER = 'OTHER',
}

export type AnnouncedReturn = {
  announcedReturns: Array<AnnouncedReturnLine>;
  announcementDate: string;
  announcementReference: string;
  carrierName: string;
  carrierServiceName: string;
  customerUUID: string;
  returnCountryId: number;
  returnLocation: ReturnLocations;
  shippingCost: number;
  trackingCode: string;
  trackingUrl: string;
  originalPaymentMethod?: string;
};

export type AnnouncedReturnLine = {
  qty: number;
  returnReasonFreeText?: string;
  returnReasonId: ReturnReason['returnReasonId'];
  saleId: string;
  saleLineId: string;
};

export type ReturnedSale = {
  announcementId?: number;
  announcementReference?: string;
  date?: number;
  lines: Array<ReturnedSaleLine>;
  orderId?: string;
  returnCountryId?: number;
  returnFromMultipleSales: boolean;
  returnLocation?: ReturnLocations;
  returnOption?: ReturnOption;
  returnOptionId?: number;
  saleId?: string;
  statusMessage: ReturnedSaleStatusTypes;
  originalPaymentMethod?: string;
};

export enum ReturnedSaleStatusTypes {
  ANNOUNCED = 'announced',
  COMPLETED = 'completed',
}

export type ReturnedSaleLine = {
  brand: string;
  idSaleLineReturned?: string;
  markedAsReturn: boolean;
  picture: string;
  productCode: string;
  productColour: string;
  productDescription: string;
  productSize: string;
  quantity: number;
  returnReasonCode?: ReturnReason['returnReasonCode'];
  returnReasonDescription: ReturnReason['description'];
  returned: boolean;
  saleLineId: string;
  sellPrice: number;
  skuId: number;
  standardPrice?: number;
  totalSellPrice: number;
  totalStandardPrice: number;
  typeId: ReturnedSaleLineStatusTypes;
  url: string;
  originalPaymentMethod?: string;
};

export enum ReturnedSaleLineStatusTypes {
  ANNOUNCED = 'P',
  RETURNED = 'R',
}

export type ReturnOption = {
  costPrice?: number;
  courier: ReturnLocations;
  dateModified?: string;
  returnOptionId: number;
  sortOrder?: number;
  hasLabel?: boolean;
};

export type ConfiguredReturnOption = {
  logo?: ImagePathWithRenditions;
  title: string;
  subtitle: string;
  courier: ReturnLocations;
  freeText: string;
  showMultipleInvoiceMessage?: boolean;
  showDifferentAnnouncedFreeText?: boolean;
  announcedFreeText?: string;
  priceOverride?: string;
  successMessage?: string;
  hideStoreNotification?: boolean;
};

export type MergedReturnOption = ConfiguredReturnOption & {
  costPrice?: number;
  returnOptionId?: number;
  hasLabel?: boolean;
  refundInformationMessageType?: string;
  hasMultipleInvoices?: boolean;
};

export type OrderLine = LineBase & {
  brandDescription: string;
  brandId: number;
  colourCode: string;
  colourDescription: string;
  discount: number;
  orderId: number;
  orderLineId: number;
  productId: number;
  sizeCode: string;
  sizeId: number;
  standardPrice: number;
  totalDiscount: number;
  totalPrice: number;
  webName: string;
};

export type ReturnPortalLoginContainerProps = {
  returnItemsPath: string;
  orderOverviewPath: string;
  accountRegistrationPath: string;
  enableLoginForm: boolean;
  loginForm: {
    sectionTitle?: string;
    image?: ImagePathWithRenditions;
  };
};

export type ReturnItemsContainerProps = {
  configuredReturnOptions?: Array<ConfiguredReturnOption>;
};

export enum ReturnTabPanes {
  RETURN_ITEMS,
  REGISTERED_RETURNS,
}

export type OrderOverviewContainerProps = {
  title?: string;
  returnItemsPath: string;
};
